/** Подписки — регулярные траты. Отдельного таба им не досталось: табов три, и
 *  четвёртый пришлось бы придумывать. Живут строкой на Главной, рядом с лимитом,
 *  потому что это тоже ответ на «сколько у меня уходит».
 *
 *  Здесь закрываются три вещи, которых в чате нет: период правится, пауза
 *  отменяется, годовая видна и как есть, и в пересчёте на месяц.
 */
import { useEffect, useState } from "react";
import {
  ApiError, api,
  type Failure, type Subscription, type Subscriptions as Data,
} from "../lib/api.ts";
import { daysUntil, dueBadge, isoDay } from "../lib/due.ts";
import { formatMoney } from "../lib/money.ts";
import { rowButton } from "../lib/rowStyle.ts";
import { plural } from "../lib/plural.ts";
import { tap } from "../lib/telegram.ts";
import { useOnce } from "../lib/once.ts";
import { Sheet } from "../components/Sheet.tsx";
import { Switch } from "../components/Switch.tsx";
import { Fallback } from "./Fallback.tsx";
import { AddButton, SheetRow, card } from "../components/ui.tsx";
import { Header } from "../components/Header.tsx";

export function Subscriptions({ onBack }: { onBack: () => void }) {
  const [data, setData] = useState<Data | null>(null);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [loading, setLoading] = useState(true);
  const [opened, setOpened] = useState<Subscription | null>(null);
  const [adding, setAdding] = useState(false);

  const load = () => {
    setLoading(true);
    setFailure(null);
    api.subscriptions().then(setData)
      .catch((e) => setFailure(e instanceof ApiError ? e.kind : "broken"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  if (failure) return <Fallback kind={failure} onRetry={load} />;

  return (
    <div style={{ padding: "var(--screen)" }}>
      <Header title="Подписки" onBack={onBack}
              action={<AddButton label="Новая подписка" onPick={() => setAdding(true)} />} />

      {loading || !data ? <Skeleton /> : data.active.length + data.paused.length === 0 ? (
        <Empty onAdd={() => setAdding(true)} />
      ) : (
        <>
          <section style={card}>
            <div className="num" style={{ fontSize: "var(--f-t1)", fontWeight: 700,
                                          letterSpacing: "-0.8px" }}>
              {formatMoney(data.monthly_total, data.base_currency)}
            </div>
            <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)", marginTop: 3 }}>
              в месяц · {plural(data.active.length, ["подписка", "подписки", "подписок"])}
            </div>
          </section>

          {data.active.length > 0 && (
            <>
              <h2 style={heading}>Ближайшие списания</h2>
              <div style={{ ...card, padding: "4px var(--s-4)" }}>
                {sortByDue(data.active).map((s, i) => (
                  <SubRow key={s.id} sub={s} base={data.base_currency}
                          last={i === data.active.length - 1} onOpen={() => setOpened(s)} />
                ))}
              </div>
            </>
          )}

          {data.paused.length > 0 && (
            <>
              <h2 style={heading}>На паузе</h2>
              <div style={{ ...card, padding: "4px var(--s-4)" }}>
                {data.paused.map((s, i) => (
                  <SubRow key={s.id} sub={s} base={data.base_currency}
                          last={i === data.paused.length - 1} onOpen={() => setOpened(s)} />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {adding && <AddSheet onClose={() => setAdding(false)}
                           onDone={() => { setAdding(false); load(); }} />}
      {opened && (
        <SubSheet sub={opened} onClose={() => setOpened(null)}
                  onDone={() => { setOpened(null); load(); }} />
      )}
    </div>
  );
}

function SubRow({ sub, base, last, onOpen }: {
  sub: Subscription; base: string; last: boolean; onOpen: () => void;
}) {
  const soon = daysUntil(sub.next_due);
  return (
    <button type="button" onClick={() => { tap(); onOpen(); }}
      // Приглушения нет: «на паузе» уже написано словом, а 0.55 роняло
      // подпись до 3.19 — ниже читаемого.
      style={rowButton(last)}>
      <div style={{ flexGrow: 1, minWidth: 0 }}>
        <div style={{ fontSize: "var(--f-body)", fontWeight: 500 }}>
          {sub.name}
          {sub.active && dueBadge(soon) && (
            <span style={{ fontSize: "var(--f-micro)", color: "var(--warn)",
                           background: "color-mix(in srgb, var(--warn) 12%, transparent)",
                           borderRadius: 5, padding: "2px 6px", marginLeft: 7 }}>
              {dueBadge(soon)}
            </span>
          )}
        </div>
        <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)", marginTop: 2 }}>
          {sub.active ? `${formatDay(sub.next_due)} · ${sub.period_label}` : "на паузе"}
        </div>
      </div>
      <div style={{ textAlign: "right" }}>
        <div className="num" style={{ fontSize: "var(--f-body)", fontWeight: 500 }}>
          {formatMoney(sub.amount, sub.currency)}
        </div>
        {sub.period === "yearly" && (
          <div className="num" style={{ fontSize: "var(--f-caption-s)", color: "var(--text-muted)" }}>
            {formatMoney(sub.monthly_amount, base)} в месяц
          </div>
        )}
      </div>
    </button>
  );
}

function SubSheet({ sub, onClose, onDone }: {
  sub: Subscription; onClose: () => void; onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState("");
  const [paidOn, setPaidOn] = useState(isoDay());
  const once = useOnce();
  const run = (call: () => Promise<unknown>) => {
    tap();
    setTrouble("");
    once(() => {
      setBusy(true);
      // Раньше отказ просто отжимал кнопку: «Оплачено», «На паузу» и «Удалить»
      // молчали, и человек не знал, случилось что-то или нет.
      return call().then(onDone)
        .catch(() => setTrouble("Не получилось. Попробуй ещё раз."))
        .finally(() => setBusy(false));
    });
  };

  return (
    <Sheet title={sub.name} onClose={onClose}>
      <div style={{ textAlign: "center", marginBottom: "var(--s-5)" }}>
        <span className="num" style={{ fontSize: "var(--f-t1)", fontWeight: 700,
                                       letterSpacing: "-0.8px" }}>
          {formatMoney(sub.amount, sub.currency)}
        </span>
        <span style={{ fontSize: "var(--f-t3)", color: "var(--text-muted)" }}>
          {" "}{sub.period_label}
        </span>
      </div>

      <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)",
                    marginBottom: "var(--s-2)" }}>
        Период
      </div>
      <div style={{ display: "flex", gap: "var(--s-2)", marginBottom: "var(--s-4)" }}>
        {(["monthly", "yearly"] as const).map((p) => (
          <button key={p} type="button" aria-disabled={busy || undefined}
                  aria-pressed={p === sub.period}
                  onClick={() => run(() => api.patchSubscription(sub.id, { period: p }))}
                  style={{ flexGrow: 1, minHeight: 38, borderRadius: "var(--r-sm)", border: "none",
                           cursor: "pointer", font: "inherit", fontSize: "var(--f-body-s)",
                           fontWeight: p === sub.period ? 600 : 400,
                           background: p === sub.period ? "var(--accent)" : "var(--surface-2)",
                           color: p === sub.period ? "var(--on-accent)" : "var(--text)" }}>
            {p === "monthly" ? "В месяц" : "В год"}
          </button>
        ))}
      </div>

      <div style={{ background: "var(--surface)", borderRadius: "var(--r-card)",
                    padding: "2px var(--s-4)" }}>
        <SheetRow label="Списание" value={formatDay(sub.next_due)} />
        <SheetRow label="Категория" value={sub.category_name} last />
      </div>

      {/* Автосписание — главный переключатель подписки, поэтому стоит выше
          кнопок. Выключен — реестр снова требует ручной отметки, и человек
          должен видеть это как свой выбор, а не как поведение бота. */}
      <div style={{ display: "flex", alignItems: "center", gap: "var(--s-3)",
                    minHeight: "var(--tap)", marginTop: "var(--s-4)",
                    padding: "10px var(--s-4)", borderRadius: "var(--r-field)",
                    background: "var(--surface-2)" }}>
        <span id={`auto-${sub.id}`} style={{ flexGrow: 1, fontSize: "var(--f-body)" }}>
          Списывать самому
        </span>
        <Switch on={sub.auto_charge} busy={busy} labelledBy={`auto-${sub.id}`}
                onToggle={() => run(() =>
                  api.patchSubscription(sub.id, { auto_charge: !sub.auto_charge }))} />
      </div>
      <p style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)",
                  lineHeight: 1.55, margin: "var(--s-2) 0 0" }}>
        {sub.auto_charge
          ? "Запишу трату в день списания и пришлю карточку. Заплатил раньше срока — отметь это датой ниже, тогда в срок дублировать не буду."
          : "Жду отметки руками: пока её нет, трата в статистику не попадёт."}
      </p>

      {/* Отметка платежа есть и при автосписании: срок 13-го, а ипотеку платят
          первого, как появились деньги. Дата — день, КОГДА ДЕНЬГИ УШЛИ, иначе
          трата легла бы сегодняшним числом. Срок после отметки уезжает на
          следующий период, поэтому автосписание платёж не продублирует. */}
      <div style={{ display: "flex", gap: "var(--s-2)", marginTop: "var(--s-4)" }}>
        <input type="date" value={paidOn} max={isoDay()}
               onChange={(e) => setPaidOn(e.target.value)} aria-label="День платежа"
               style={{ ...field, flexGrow: 1, minWidth: 0 }} />
        <button type="button" aria-disabled={busy || undefined}
                onClick={() => run(() => api.paySubscription(sub.id, paidOn))}
                style={{ ...action, width: "auto", flexShrink: 0,
                         padding: "14px var(--s-5)", background: "var(--surface-2)" }}>
          Оплачено
        </button>
      </div>
      <button type="button" aria-disabled={busy || undefined}
              onClick={() => run(() => api.patchSubscription(sub.id, { active: !sub.active }))}
              style={{ ...action, marginTop: "var(--s-2)", background: "var(--surface-2)" }}>
        {sub.active ? "Поставить на паузу" : "Вернуть в реестр"}
      </button>
      <button type="button" aria-disabled={busy || undefined}
              onClick={() => run(() => api.deleteSubscription(sub.id))}
              style={{ ...action, marginTop: "var(--s-2)", background: "transparent",
                       color: "var(--danger)",
                       border: "1px solid color-mix(in srgb, var(--danger) 30%, transparent)" }}>
        Удалить из реестра
      </button>
      {trouble && <p role="alert" style={{ fontSize: "var(--f-caption)", color: "var(--warn)",
                                          lineHeight: 1.55, marginTop: "var(--s-3)",
                                          marginBottom: 0 }}>{trouble}</p>}
      <p style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)",
                  lineHeight: 1.55, marginTop: "var(--s-4)", marginBottom: 0 }}>
        Удаление убирает подписку из реестра. Записанные ранее траты остаются — это факты.
      </p>
    </Sheet>
  );
}


function Empty({ onAdd }: { onAdd: () => void }) {
  return (
    <div style={{ ...card, textAlign: "center", padding: "var(--s-8) var(--s-5)" }}>
      <p style={{ fontSize: "var(--f-body)", fontWeight: 600, margin: 0 }}>Подписок пока нет</p>
      <p style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)", lineHeight: 1.55,
                  marginTop: "var(--s-2)", marginBottom: "var(--s-5)" }}>
        Скажи боту «подписка нетфликс 12 долларов в месяц» — или заведи здесь.
      </p>
      <button type="button" onClick={() => { tap(); onAdd(); }}
              style={{ width: "100%", minHeight: "var(--tap)", padding: 14, border: "none",
                       borderRadius: "var(--r-field)", cursor: "pointer", font: "inherit",
                       fontSize: "var(--f-body)", fontWeight: 600,
                       background: "var(--accent)", color: "var(--on-accent)" }}>
        Завести подписку
      </button>
    </div>
  );
}

/** Форма подписки. Дату следующего платежа спрашиваем, а не считаем: «наверное
 *  через месяц» поставило бы в реестр напоминание на дату, которой человек не
 *  называл. Платёж при заведении не пишется — человек сейчас ничего не платил. */
function AddSheet({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [period, setPeriod] = useState<"monthly" | "yearly">("monthly");
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState("");

  const ready = name.trim() && amount.trim() && due;

  const once = useOnce();

  const submit = () => {
    tap();
    setBusy(true);
    setTrouble("");
    once(() => api.addSubscription({ name: name.trim(), amount: amount.trim(), currency, period,
                          next_due: due })
      .then(onDone)
      .catch(() => setTrouble("Не получилось сохранить. Проверь сумму и дату."))
      .finally(() => setBusy(false)));
  };

  return (
    <Sheet title="Новая подписка" onClose={onClose}>
      <Field label="Название">
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120}
               placeholder="Нетфликс" aria-label="Название" style={field} />
      </Field>
      <Field label="Сколько списывают">
        <div style={{ display: "flex", gap: "var(--s-2)" }}>
          <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal"
                 placeholder="12" aria-label="Сумма" style={{ ...field, flexGrow: 1 }} />
          <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                  aria-label="Валюта" style={{ ...field, width: "auto", padding: "0 12px" }}>
            <option value="USD">$</option>
            <option value="VND">₫</option>
            <option value="RUB">₽</option>
          </select>
        </div>
      </Field>
      <Field label="Как часто">
        <div style={{ display: "flex", gap: "var(--s-2)" }}>
          {([["monthly", "В месяц"], ["yearly", "В год"]] as const).map(([key, label]) => (
            <button key={key} type="button" aria-pressed={period === key}
                    onClick={() => setPeriod(key)}
                    style={{ flexGrow: 1, minHeight: 44, border: "none", cursor: "pointer",
                             borderRadius: "var(--r-sm)", font: "inherit",
                             fontSize: "var(--f-body-s)", fontWeight: period === key ? 600 : 400,
                             background: period === key ? "var(--accent)" : "var(--surface-2)",
                             color: period === key ? "var(--on-accent)" : "var(--text)" }}>
              {label}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Следующее списание">
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)}
               aria-label="Следующее списание" style={field} />
      </Field>
      {trouble && <p role="alert" style={{ fontSize: "var(--f-caption)", color: "var(--warn)",
                                           lineHeight: 1.55 }}>{trouble}</p>}
      <button type="button" disabled={!ready || busy} onClick={submit}
              style={{ width: "100%", minHeight: "var(--tap)", marginTop: "var(--s-4)",
                       padding: 14, borderRadius: "var(--r-field)", border: "none",
                       cursor: "pointer", font: "inherit", fontSize: "var(--f-body)",
                       fontWeight: 600,
                       background: ready && !busy ? "var(--accent)" : "var(--surface-2)",
                       color: ready && !busy ? "var(--on-accent)" : "var(--text-disabled)" }}>
        Завести
      </button>
      <p style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)", lineHeight: 1.55,
                  marginTop: "var(--s-3)", marginBottom: 0 }}>
        Трата сейчас не запишется: подписка попадёт в реестр, а платёж отметится кнопкой
        «Оплачено», когда он случится.
      </p>
    </Sheet>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: "var(--s-4)" }}>
      <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)",
                    marginBottom: "var(--s-2)" }}>{label}</div>
      {children}
    </div>
  );
}

const field: React.CSSProperties = {
  width: "100%", minHeight: "var(--tap)", background: "var(--surface-2)", border: "none",
  borderRadius: "var(--r-field)", padding: "12px 14px", color: "var(--text)",
  font: "inherit", fontSize: "var(--f-body)",
};

function Skeleton() {
  return (
    <div style={card} role="status" aria-busy="true" aria-label="Загружаю подписки">
      <div style={{ height: 30, width: "40%", background: "var(--surface-3)", borderRadius: 7 }} />
      <div style={{ height: 11, width: "55%", background: "var(--surface-2)", borderRadius: 5,
                    marginTop: 10 }} />
    </div>
  );
}

function sortByDue(subs: Subscription[]): Subscription[] {
  // По дате списания, а не по алфавиту: важно «что спишется завтра».
  return [...subs].sort((a, b) => a.next_due.localeCompare(b.next_due));
}

const MONTHS = ["января", "февраля", "марта", "апреля", "мая", "июня",
                "июля", "августа", "сентября", "октября", "ноября", "декабря"];

function formatDay(iso: string): string {
  const [, month, date] = iso.split("-");
  return `${Number(date)} ${MONTHS[Number(month) - 1]}`;
}


const heading: React.CSSProperties = {
  fontSize: "var(--f-t3)", fontWeight: 600, margin: "var(--s-6) 0 var(--s-3)",
};

const action: React.CSSProperties = {
  width: "100%", minHeight: "var(--tap)", padding: "14px", borderRadius: "var(--r-field)",
  border: "none", cursor: "pointer", font: "inherit", fontSize: "var(--f-body)",
  fontWeight: 600, color: "var(--text)",
};
